import { useState, useEffect, useMemo } from "react";
import { X, Search, Lock, ChevronLeft, ExternalLink, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

// Polls window.__reservoirExt for ~2.4s and returns `{installed, installUrl}`.
//   - {installed: undefined}        → still checking
//   - {installed: true,  installUrl}
//   - {installed: false, installUrl}
function useExtensionState() {
  const [state, setState] = useState({ installed: undefined, installUrl: null });
  // Detection loop.
  useEffect(() => {
    let attempts = 0;
    const t = setInterval(() => {
      const ext = typeof window !== "undefined" ? window.__reservoirExt : null;
      if (ext?.installed) {
        setState((s) => ({ ...s, installed: true }));
        clearInterval(t);
        return;
      }
      if (++attempts > 12) {
        setState((s) => ({ ...s, installed: false }));
        clearInterval(t);
      }
    }, 200);
    return () => clearInterval(t);
  }, []);
  // Manifest fetch (one-shot).
  useEffect(() => {
    let cancelled = false;
    api.get("/extension/manifest")
      .then(({ data }) => { if (!cancelled) setState((s) => ({ ...s, installUrl: data?.install_url || null })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return state;
}

export default function AddProgramModal({ onClose, onAdd, onAwConnected }) {
  // mode: "master" (catalog picker) | "custom" (manual entry)
  const [mode, setMode] = useState("master");
  const ext = useExtensionState();

  // -------- Master list state ---------------------------------------------
  const [master, setMaster]               = useState([]);
  const [masterLoading, setMasterLoading] = useState(true);
  const [masterSearch, setMasterSearch]   = useState("");
  const [pickedMaster, setPickedMaster]   = useState(null);

  // Existing user programs — used to block adding a duplicate (code × holder)
  // row. The user re-syncs via the per-card button, not via "Add a program".
  const [existingPrograms, setExistingPrograms] = useState([]);

  useEffect(() => {
    api.get("/master-list").then(({ data }) => {
      setMaster(data.programs || []);
    }).catch(() => setMaster([])).finally(() => setMasterLoading(false));
    api.get("/programs").then(({ data }) => {
      setExistingPrograms(Array.isArray(data) ? data : (data?.programs || []));
    }).catch(() => setExistingPrograms([]));
  }, []);

  // -------- Custom (manual) state ----------------------------------------
  const [custom, setCustom] = useState({
    code: "", name: "", category: "airline", currency: "mi",
    balance: 0, holder: "self", transferable: false, tier: "",
  });

  // -------- Extension-connect flow state ---------------------------------
  // No credentials are stored — the modal just arms a sync + opens the
  // program's login page in a new tab. The extension watches the tab,
  // waits for the user to sign in manually, then reads the balance.
  // `extHolder` is the family-member label the user is syncing for; the
  // extension can't tell whose account is in cookies so the user owns the tag.
  const [extHolder, setExtHolder]         = useState("self");
  const [extSubmitting, setExtSubmitting] = useState(false);
  const [extError, setExtError]           = useState("");
  const [extPhase, setExtPhase]           = useState("intro");  // intro | connecting | done | error
  const [extRid, setExtRid]               = useState(null);
  const [extResult, setExtResult]         = useState(null);

  const filteredMaster = useMemo(() => {
    const needle = masterSearch.trim().toLowerCase();
    return master.filter((r) => {
      if (!needle) return true;
      const hay = `${r.name} ${r.code} ${r.recipe_code || ""}`.toLowerCase();
      return hay.includes(needle);
    }).slice(0, 100);
  }, [master, masterSearch]);

  function resetMasterPick() {
    setPickedMaster(null);
    setExtError("");
    setExtPhase("intro");
    setExtRid(null);
    setExtResult(null);
    setExtHolder("self");
  }

  // Holders already used for the picked program — used to block duplicates.
  const pickedCode = pickedMaster?.recipe_code || pickedMaster?.code;
  const existingHoldersForCode = useMemo(() => {
    if (!pickedCode) return [];
    const set = new Set();
    for (const p of existingPrograms) {
      if (p?.code !== pickedCode) continue;
      set.add((p?.holder || "self").trim() || "self");
    }
    return [...set];
  }, [existingPrograms, pickedCode]);
  const normalizedHolder = (extHolder || "").trim() || "self";
  const holderTaken = existingHoldersForCode.some(
    (h) => h.toLowerCase() === normalizedHolder.toLowerCase(),
  );

  // ----- Extension connect: arm + open login URL --------------------------
  async function submitExtConnect() {
    const code = pickedMaster?.recipe_code || pickedMaster?.code;
    if (!code) return;
    if (holderTaken) {
      setExtError(`You already have ${pickedMaster?.name || code} for "${normalizedHolder}". Re-sync that holder from its program card, or pick a different family-member label.`);
      setExtPhase("intro");
      return;
    }
    setExtSubmitting(true); setExtError("");
    try {
      const holder = normalizedHolder;
      const { data: arm } = await api.post("/net/sync/arm", { program_code: code, holder });
      const url = arm.login_url + "#" + arm.fragment;
      window.open(url, "_blank", "noopener");
      setExtRid(arm.request_id);
      setExtPhase("connecting");
      onAwConnected?.();
    } catch (e) {
      setExtError(e?.response?.data?.detail || e?.message || "couldn't start sync");
      setExtPhase("error");
    } finally {
      setExtSubmitting(false);
    }
  }

  // Poll /api/net/sync/result/{rid} every 2s while the extension is
  // signing into the program in a separate tab.
  useEffect(() => {
    if (extPhase !== "connecting" || !extRid) return undefined;
    const resultPath = `/net/sync/result/${extRid}`;
    let cancelled = false;
    const start = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    async function tick() {
      if (cancelled) return;
      try {
        const { data: job } = await api.get(resultPath);
        if (job?.status === "completed") {
          onAwConnected?.();
          onClose?.();
          return;
        }
        if (job?.status === "failed") {
          setExtError(job?.error || "The sync didn't complete. You may need to finish signing in manually in the new tab.");
          setExtPhase("error");
          return;
        }
      } catch {}
      if (Date.now() - start > TIMEOUT_MS) {
        setExtError("This is taking longer than usual. The extension will keep working in the new tab — check your dashboard in a minute.");
        setExtPhase("error");
        return;
      }
      if (!cancelled) setTimeout(tick, 2000);
    }
    tick();
    return () => { cancelled = true; };
  }, [extPhase, extRid, onAwConnected, onClose]);

  const submit = (e) => {
    e?.preventDefault();
    if (mode === "custom") onAdd({ ...custom });
  };

  // ---------- Render helpers ----------------------------------------------
  const renderExtensionMissingBanner = () => (
    <div
      className="mb-3 p-3 rounded-[10px] border border-amber-200 bg-amber-50 flex items-start gap-2.5"
      data-testid="addprog-ext-missing"
    >
      <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] text-amber-900">
          Install the Reservoir extension to add programs
        </div>
        <div className="text-[12px] text-amber-900/80 mt-0.5 leading-relaxed">
          The browser extension is required to sign you in and read your loyalty
          balance. Install it once, then come back here.
        </div>
        {ext.installUrl && (
          <a
            href={ext.installUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary text-[12.5px] mt-2 inline-flex items-center gap-1.5"
            data-testid="addprog-ext-install-btn"
          >
            <ExternalLink size={12} /> Install from the Chrome Web Store
          </a>
        )}
      </div>
    </div>
  );

  const renderMasterCatalog = () => (
    <div data-testid="addprog-master-pane">
      {ext.installed === false && renderExtensionMissingBanner()}
      {masterLoading ? (
        <div className="text-[13px] text-neutral-500 flex items-center gap-2 py-6">
          <span className="spinner" /> Loading catalog…
        </div>
      ) : master.length === 0 ? (
        <div className="p-4 border border-black/10 rounded-[10px] text-[13px] text-neutral-700">
          No programs in the catalog yet. An admin needs to add them under Admin → Master List.
        </div>
      ) : !pickedMaster ? (
        <>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              className="input pl-9"
              placeholder="Search programs…"
              value={masterSearch}
              onChange={(e) => setMasterSearch(e.target.value)}
              data-testid="addprog-master-search"
            />
          </div>
          <div className="max-h-[360px] overflow-auto pr-1 border border-black/5 rounded-[10px] divide-y divide-black/5">
            {filteredMaster.length === 0 ? (
              <div className="text-[12.5px] text-neutral-500 p-4">No programs match.</div>
            ) : (
              filteredMaster.map((p) => (
                <button
                  key={p.id || p.code}
                  onClick={() => setPickedMaster(p)}
                  className="w-full text-left px-3 py-2.5 hover:bg-black/[0.02] text-[13px]"
                  data-testid={`addprog-master-${p.code}`}
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{p.name}</div>
                    <div className="text-[10.5px] text-neutral-500 font-mono truncate">{p.code} · {p.category}</div>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="flex items-start gap-2 mt-3 text-[11.5px] text-neutral-500">
            <Lock size={11} className="mt-0.5 shrink-0" />
            <span>Pick a program — we'll open its login page in a new tab and the Reservoir extension reads your balance once you're signed in.</span>
          </div>
        </>
      ) : extPhase === "connecting" ? (
        <ExtConnectingView row={pickedMaster} />
      ) : extPhase === "done" ? (
        <ExtDoneView row={pickedMaster} extracted={extResult?.extracted || {}} />
      ) : extPhase === "error" ? (
        <ExtErrorView
          row={pickedMaster}
          error={extError}
          onRetry={() => { setExtPhase("intro"); setExtError(""); setExtRid(null); setExtResult(null); }}
        />
      ) : (
        <ExtIntroView
          row={pickedMaster}
          onBack={resetMasterPick}
          error={extError}
          holder={extHolder}
          onHolderChange={setExtHolder}
          existingHolders={existingHoldersForCode}
          holderTaken={holderTaken}
          normalizedHolder={normalizedHolder}
        />
      )}
    </div>
  );

  // ---------- Modal frame -------------------------------------------------
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 fade-in"
      style={{ background: "rgba(10,10,10,0.45)" }}
      onClick={extPhase === "connecting" ? undefined : onClose}
      data-testid="add-program-modal"
    >
      <div className="card w-full max-w-2xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-neutral-400 hover:text-black" aria-label="Close">
          <X size={18} />
        </button>
        <div className="font-display font-bold text-lg tracking-tight">Add a loyalty program</div>
        <p className="text-[13px] text-neutral-600 mt-1">
          Pick a program from the catalog — Reservoir will read your balance via the browser extension.
        </p>

        <div className="flex gap-2 mt-4 mb-3 text-[13px]">
          <button onClick={() => setMode("master")} className={`btn ${mode === "master" ? "btn-primary" : "btn-ghost"} text-[13px]`} data-testid="addprog-tab-master">
            Catalog
          </button>
          <button onClick={() => setMode("custom")} className={`btn ${mode === "custom" ? "btn-primary" : "btn-ghost"} text-[13px]`} data-testid="addprog-tab-custom">
            Custom (manual)
          </button>
        </div>

        {mode === "master" ? renderMasterCatalog() : (
          <form className="space-y-3" onSubmit={submit}>
            <input className="input" placeholder="Program name (e.g. Iberia Plus)" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value, code: e.target.value.toLowerCase().replace(/\W+/g, "_") })} required />
            <div className="grid grid-cols-2 gap-3">
              <select className="input" value={custom.category} onChange={(e) => setCustom({ ...custom, category: e.target.value })}>
                <option value="airline">Airline</option>
                <option value="hotel">Hotel</option>
                <option value="bank">Bank / transferable</option>
                <option value="other">Other</option>
              </select>
              <select className="input" value={custom.currency} onChange={(e) => setCustom({ ...custom, currency: e.target.value })}>
                <option value="pts">Points</option>
                <option value="mi">Miles</option>
              </select>
              <input className="input tabular" type="number" placeholder="0" value={custom.balance} onChange={(e) => setCustom({ ...custom, balance: Number(e.target.value) })} />
              <input className="input" placeholder="Tier (optional)" value={custom.tier} onChange={(e) => setCustom({ ...custom, tier: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] text-neutral-500 mb-1 block">Holder / family member</label>
              <input className="input" placeholder="self · spouse · Mom · etc." value={custom.holder} onChange={(e) => setCustom({ ...custom, holder: e.target.value || "self" })} data-testid="addprog-custom-holder" />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-neutral-700">
              <input type="checkbox" checked={custom.transferable} onChange={(e) => setCustom({ ...custom, transferable: e.target.checked })} />
              Transferable currency (counts toward "Transferable" total)
            </label>
          </form>
        )}

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-black/5">
          {mode === "master" ? (
            !pickedMaster ? (
              <button onClick={onClose} className="btn btn-ghost text-[13px]">Cancel</button>
            ) : extPhase === "connecting" ? (
              <button onClick={onClose} className="btn btn-ghost text-[13px]" data-testid="addprog-ext-bg-btn">
                Continue in background
              </button>
            ) : extPhase === "done" || extPhase === "error" ? (
              <button onClick={onClose} className="btn btn-primary text-[13px]" data-testid="addprog-ext-done-btn">
                Done
              </button>
            ) : (
              <>
                <button onClick={resetMasterPick} className="btn btn-ghost text-[13px]">Back</button>
                <button
                  onClick={submitExtConnect}
                  disabled={extSubmitting || ext.installed === false || holderTaken}
                  className="btn btn-primary text-[13px]"
                  data-testid="addprog-ext-submit-btn"
                  title={
                    ext.installed === false
                      ? "Install the Reservoir extension first"
                      : holderTaken
                      ? `Already have this program for "${normalizedHolder}"`
                      : undefined
                  }
                >
                  {extSubmitting ? <><span className="spinner" /> Opening…</> : "Open & connect"}
                </button>
              </>
            )
          ) : (
            <>
              <button onClick={onClose} className="btn btn-ghost text-[13px]">Cancel</button>
              <button onClick={submit} disabled={!custom.name} className="btn btn-primary text-[13px]" data-testid="addprog-save-btn">Add program</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- Helper child components -----------------------------------------
function BackButton({ onClick }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 text-[12px] text-neutral-500 hover:text-black -mt-1 mb-1">
      <ChevronLeft size={12} /> Back to catalog
    </button>
  );
}

function ExtConnectingView({ row }) {
  return (
    <div className="p-6 text-center" data-testid="addprog-ext-connecting">
      <div className="spinner mx-auto mb-4" style={{ width: 28, height: 28, borderWidth: 3 }} />
      <div className="font-display font-bold text-[15px] tracking-tight">
        Signing into {row.name}…
      </div>
      <p className="text-[12.5px] text-neutral-500 mt-2 leading-relaxed max-w-sm mx-auto">
        We opened {row.name} in a new tab. Sign in there as you normally would — the Reservoir extension is watching that tab and will read your balance once you're signed in.
      </p>
      <p className="text-[11.5px] text-neutral-400 mt-3 leading-relaxed max-w-sm mx-auto">
        Finish any MFA / security prompt in the new tab — we'll keep watching.
      </p>
    </div>
  );
}

function ExtDoneView({ row, extracted }) {
  const balance = extracted?.balance != null ? Number(extracted.balance).toLocaleString() : null;
  const tier = extracted?.tier && extracted.tier !== "—" ? extracted.tier : "";
  return (
    <div className="p-5 border border-emerald-200 bg-emerald-50 rounded-[10px]" data-testid="addprog-ext-success">
      <div className="font-display font-bold text-[14px] text-emerald-900">
        {balance ? <>Connected · {balance} {row.currency === "mi" ? "miles" : "points"}</> : <>Connected · {row.name}</>}
      </div>
      <p className="text-[12.5px] text-emerald-900/80 mt-1.5 leading-relaxed">
        {row.name}{tier ? ` · ${tier}` : ""}. Click "Sync via extension" later to refresh — you'll sign in again in a new tab.
      </p>
    </div>
  );
}

function ExtErrorView({ row, error, onRetry }) {
  return (
    <div className="p-5 border rounded-[10px]" style={{ background: "var(--bad-bg)", borderColor: "var(--bad-border)" }} data-testid="addprog-ext-failed">
      <div className="font-display font-bold text-[14px]" style={{ color: "var(--bad)" }}>
        Couldn't finish signing in
      </div>
      <p className="text-[12.5px] mt-1.5 leading-relaxed" style={{ color: "var(--bad)" }}>
        {error || `We opened ${row.name} in a new tab but didn't get a balance back. Make sure you finished signing in in that tab, then hit retry.`}
      </p>
      <div className="flex gap-2 mt-3">
        <button onClick={onRetry} className="btn btn-ghost text-[12.5px]" data-testid="addprog-ext-retry-btn">
          Try again
        </button>
      </div>
    </div>
  );
}

function ExtIntroView({ row, onBack, error, holder, onHolderChange, existingHolders, holderTaken, normalizedHolder }) {
  const holders = Array.isArray(existingHolders) ? existingHolders : [];
  return (
    <div className="space-y-3" data-testid="addprog-ext-intro">
      <BackButton onClick={onBack} />
      <div className="p-3 bg-black/[0.03] rounded-[10px] border border-black/5">
        <div className="font-semibold text-[13px]">{row.name}</div>
        <div className="text-[11px] text-neutral-500 font-mono">{row.code}</div>
      </div>
      <div>
        <label className="text-[11px] text-neutral-500 mb-1 block">
          Holder / family member <span className="text-neutral-400">(label this account so multiple family members can share a program)</span>
        </label>
        <input
          className={`input ${holderTaken ? "border-red-400 focus:border-red-500" : ""}`}
          placeholder="self · spouse · Mom · Dad · Kid"
          value={holder || ""}
          onChange={(e) => onHolderChange?.(e.target.value)}
          data-testid="addprog-ext-holder"
        />
        {holders.length > 0 && (
          <div className="text-[10.5px] text-neutral-500 mt-1.5 leading-relaxed">
            Already added for {row.name}:{" "}
            {holders.map((h, i) => (
              <span key={h} className="font-mono text-neutral-700">
                {i > 0 && ", "}
                {h}
              </span>
            ))}
            . Pick a different label.
          </div>
        )}
        {holderTaken ? (
          <div className="text-[11.5px] mt-1.5 p-2 rounded-[6px] bg-red-50 border border-red-200 text-red-700 flex gap-1.5 items-start" data-testid="addprog-ext-holder-error">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>
              You already have <span className="font-mono">{row.name}</span> for <span className="font-mono">"{normalizedHolder}"</span>. Re-sync from that program's card, or pick a different family-member label.
            </span>
          </div>
        ) : (
          <div className="text-[10.5px] text-neutral-500 mt-1 leading-relaxed">
            Sign into the account belonging to <span className="font-mono">{(holder || "self").trim() || "self"}</span> when the tab opens — this label is the tag we save under, but the actual account synced is whoever's signed in there.
          </div>
        )}
      </div>
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-[10px] text-[12px] text-emerald-900 leading-relaxed">
        We'll open <span className="font-mono">{row.name}</span> in a new tab. Sign in there as you normally would — the Reservoir extension waits in that tab and reads your balance once you're signed in. Reservoir never sees or stores your password.
      </div>
      {error && (
        <div className="text-[12.5px] p-2.5 rounded-[8px]" style={{ background: "var(--bad-bg)", color: "var(--bad)" }}>{error}</div>
      )}
      <div className="flex items-start gap-2 text-[11px] text-neutral-500 pt-1">
        <Lock size={11} className="mt-0.5 shrink-0" />
        <span>Personally-identifying values in the page (name, email, account number) are redacted in your browser before any analysis.</span>
      </div>
    </div>
  );
}
